import React, {useState, useEffect, useContext} from 'react';
import {
  KeyboardAvoidingView,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import * as Keychain from 'react-native-keychain';
import ReactNativeBiometrics from 'react-native-biometrics';
import {CommonActions} from '@react-navigation/native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Button from '../../components/elements/Button';
import Header from '../../components/elements/Header';
import Divider from '../../components/elements/Divider';
import Popup from '../../components/elements/Popup';
import Text from '../../components/elements/Text';
import SignInInputForm from '../../components/modules/SignInInputForm';
import FaceIdDisableDrawer from '../../components/modules/FaceIdDisableDrawer';
import FaceIdIdentfyingDrawer from '../../components/modules/FaceIdIdentfyingDrawer';
import FaceIdSuccess from '../../components/modules/FaceIdSuccess';
import FaceIdFailed from '../../components/modules/FaceIdFailed';
import FaceIdNotRecognized from '../../components/modules/FaceIdNotRecognized';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {fetchApi, NERWORK_ERROR} from '../../utils/ApiHelper';
import {HTTP_METHODS, LOGIN} from '../../utils/URLConstants';
import {
  BUTTON_TYPES,
  CONDITIONS,
  DISABLE_ERROR,
  KEYS,
  UNVERIFIED_ERROR,
  USER_AUTH_STATUS,
} from '../../utils/Constants';
import {
  DLog,
  getBiometricLabel,
  isEmpty,
  trimSpace,
  validateEmail,
  validPassword,
  updateTokenVersion,
} from '../../utils/HelperMethods';
import {
  getData,
  removeData,
  storeData,
  storeUserProfileData,
} from '../../utils/GeneralStorage';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import AccountStatusModal from '../../components/modules/AccountStatusModal';
import {tID} from '../../utils/TestIdHelper';
import {accountStatusContext} from '../../contexts/AccountStatusContext';
import {ErrorBottomExpandableDrawer, getOTP} from '../AccountDetailsScreen';
import {setRefreshToken} from '../../utils/TokenStore';
import {useLocation} from '../../utils/mockedWebLibs/react-router-dom';
import {eligibilityCheckContext} from '../../contexts/EligibilityCheckContext';
import Config from 'react-native-config';

export const ContactSupportPopup = ({
  showContact = false,
  setShowContact = () => {},
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      title={'Contact Support'}
      visible={showContact}
      testID={'SignIn-Popup-ContactSupport'}
      description={
        <>
          It seems like you're having trouble logging in. Please reach out to{' '}
          <Text variation="Body1SemiBold" color={colors.textMuted}>
            {Config.DIATHRIVE_SUPPORT_EMAIL}
          </Text>{' '}
          or try logging in again after 1 hour.
        </>
      }>
      <Button
        style={[styles.button]}
        onPress={() => setShowContact(false)}
        title={'Close'}
        titleColor={colors.link}
        testID={tID('SignIn-Button-ContactSupportClose')}
      />
    </Popup>
  );
};

const SignIn = ({navigation, route}) => {
  const {state} = useLocation();
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);

  const [showModal, setshowModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const [supportFaceId, setSupportFaceId] = useState(false);
  const [faceIDCheckBoxSelected, setFaceIDCheckBoxSelected] = useState(false);
  const [showIdentifyingModal, setShowIdentifyingModal] = useState(false);
  const [showFaceIdSuccessModal, setShowFaceIdSuccessModal] = useState(false);
  const [showFaceIdFailedModal, setShowFaceIdFailedModal] = useState(false);
  const [showFaceIdNotRecognzedModal, setShowFaceIdNotRecognzedModal] =
    useState(false);
  const [showContactSupport, setshowContactSupport] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isValidForm, setValidForm] = useState(false);
  const [haveCreds, setHaveCreds] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [hasError, setError] = useState(false);
  const [hasEmailError, setEmailError] = useState(false);
  const [hasPasswordError, setPasswordError] = useState(false);
  const [bottomModalVisible, setBottomModalVisible] = useState(false);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);
  const asc = useContext(accountStatusContext);
  const ecc = useContext(eligibilityCheckContext);

  useEffect(() => {
    if (Platform.OS != 'web') {
      Keychain.getGenericPassword()
        .then(async creds => {
          if (creds) {
            setHaveCreds(true);

            // Populate the email and password fields from saved credentials
            setEmail(creds.username);
            setPassword(creds.password);

            // Check if Face ID is supported and auto-trigger it if enabled
            const isFaceIdEnabled = await getData(KEYS.faceIdEnabled);
            const biometrics = await ReactNativeBiometrics.isSensorAvailable();
            const hasFaceId = biometrics.available &&
              (Platform.OS === 'ios'
                ? biometrics.biometryType === ReactNativeBiometrics.FaceID
                : biometrics.biometryType === ReactNativeBiometrics.Biometrics);

            // Sync checkbox with saved state
            if (isFaceIdEnabled === 'true') {
              setFaceIDCheckBoxSelected(true);
            }

            // Only auto-trigger Face ID if not coming from sign-out
            const skipAutoFaceId = route?.params?.skipAutoFaceId;
            if (isFaceIdEnabled === 'true' && hasFaceId && !skipAutoFaceId) {
              // Delay slightly to ensure UI is ready
              setTimeout(() => {
                authenticateFaceId();
              }, 500);
            }
          } else {
            setHaveCreds(false);
          }
        })
        .catch(e => {
          setHaveCreds(false);
        });
      ReactNativeBiometrics.isSensorAvailable()
        .then(({biometryType, available}) => {
          // Success code
          if (Platform.OS === 'android') {
            if (
              available &&
              biometryType === ReactNativeBiometrics.Biometrics
            ) {
              setSupportFaceId(true);
            } else {
              setSupportFaceId(false);
            }
          } else {
            if (available && biometryType === ReactNativeBiometrics.FaceID) {
              setSupportFaceId(true);
            } else {
              setSupportFaceId(false);
            }
          }
        })
        .catch(error => {
          // Failure code
          // DLog(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only Keychain/Face-ID boot flow; must never re-run after mount
  }, []);

  useEffect(() => {
    validateSignin();
    if (hasError) {
      setError(false);
    }
    if (hasEmailError) {
      setEmailError(false);
    }
    if (hasPasswordError) {
      setPasswordError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- error-clearing is keyed to input edits only; adding the error flags would clear a submit error the moment it is set
  }, [email, password]);

  useEffect(() => {
    if (state) {
      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only check of the incoming location state to surface a prior error
  }, []);

  useEffect(() => {
    checkAttempts(); //her checkAttempts is invoked without the runFunction argument
  }, []);

  const checkAttempts = runFunction => {
    //this function checks attempts and then runs a function if supplied
    getData(KEYS.signinNumAttemptKey) //logic for making the modal appear/disappear
      .then(numAttempts => {
        if (numAttempts >= CONDITIONS.numAttemptsAllowed) {
          getData(KEYS.signinBlockedTimeKey).then(blockedTime => {
            let timeDiff = new Date() - Date.parse(blockedTime);
            if (CONDITIONS.timeDiffAllowed < timeDiff) {
              storeData(KEYS.signinNumAttemptKey, '0');
            } else {
              setShowContact(true);
            }
          });
        } else {
          if (runFunction) {
            runFunction();
          }
        }
      });
  };

  const updateNumAttempts = () => {
    getData(KEYS.signinNumAttemptKey).then(numAttempts => {
      numAttempts ? numAttempts++ : (numAttempts = 1);
      if (numAttempts == CONDITIONS.numAttemptsAllowed) {
        //set the blocker time once
        storeData(KEYS.signinBlockedTimeKey, new Date().toString());
      }
      storeData(KEYS.signinNumAttemptKey, '' + numAttempts);
    });
  };

  const onPressCheckbox = () => {
    if (supportFaceId) {
      setFaceIDCheckBoxSelected(!faceIDCheckBoxSelected);
    } else {
      setshowModal(true);
    }
  };

  const onPressShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onPressForgotPassword = async () => {
    setPassword('');
    if (Platform.OS === 'web') {
      navigation('/forgot-password');
    } else {
      navigation.navigate('ForgotPasswordPhoneVerification');
    }
  };

  const onPressSignUp = async () => {
    if (Platform.OS === 'web') {
      navigation('/sign-up');
    } else {
      navigation.navigate('SubmitEmployerId');
    }

    // navigation.navigate('DiabetesType');
  };

  const onPressTryFaceIdAgain = () => {
    setShowFaceIdFailedModal(false);
    onPressFaceId();
  };

  const onPressSignin = async () => {
    if (!validateEmail(trimSpace(email))) {
      setEmailError(true);
      return;
    }

    if (!validPassword(password)) {
      setPasswordError(true);
      return;
    }

    const postData = {
      email: trimSpace(email),
      password,
    };
    userSignin(postData);
  };

  const userSignin = postData => {
    lpc.setVisible(true); //shows up evn without this
    // Dlog(postData);
    fetchApi(HTTP_METHODS.POST, LOGIN, postData)
      .then(async res => {
        if (res.status === 200 || res.status === 304) {
          storeData(KEYS.signinNumAttemptKey, '0');
          if (Platform.OS != 'web') {
            await Keychain.setGenericPassword(trimSpace(email), password);
            if (faceIDCheckBoxSelected) {
              await storeData(
                KEYS.faceIdEnabled,
                String(faceIDCheckBoxSelected),
              );
            } else {
              await removeData(KEYS.faceIdEnabled);
            }
          }
          const profile = res.data.data;
          // DLog('got the profile as ', profile);
          // Prefer the short-lived accessToken (auto-refreshed by the ApiHelper interceptor);
          // fall back to the legacy non-expiring token until the backend auth update ships.
          await storeData(KEYS.userToken, profile.accessToken || profile.token);
          if (profile.refreshToken) {
            await setRefreshToken(profile.refreshToken);
          }

          await storeData(KEYS.isLogin, USER_AUTH_STATUS.login);
          await storeData(KEYS.userId, profile.id);
          await storeUserProfileData(profile);
          await updateTokenVersion();
          asc.setOnboarded(!!profile.coachingOnboarded);
          lpc.setVisible(false);
          //checking user employer needs eligibility check or not
          if (profile.employer.realtimeFlowId === 0) {
            //redirecting to normal flow if user employer doesn't need eligibility check
            await storeData(KEYS.userEligible, 'true');
            ecc.setIsEligible(true);
          } else {
            //checking Eligibilty based on employer realtimeFlowId
            ecc.checkEligibilty(
              navigation,
              profile.firstname + ' ' + profile.lastname,
              profile.email,
              profile.dateOfBirth,
              profile.subscriberId,
              profile.employer.realtimeFlowId,
            );
          }

          //navigating based on eligibility check criteria
          if (Platform.OS === 'web') {
            const targetRoute = profile.employer.realtimeFlowId === 0 ? '/home' : '/store';
            navigation(targetRoute);
          } else {
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [
                  {
                    name: 'HomeTab',
                    params: {
                      screen:
                        profile.employer.realtimeFlowId === 0
                          ? 'HomePage'
                          : 'Shopping',
                    },
                  },
                ],
              }),
            );
          }
        } else {
          updateNumAttempts();
          lpc.setVisible(false);
        }
      })
      .catch(e => {
        // Dlog('error is', e);
        lpc.setVisible(false);
        // Prefer the backend's machine-readable `code`; fall back to the legacy message
        // match for one release while the backend `code` change rolls out. See AUTH-FIX-PLAN.md.
        const code = e && e.code;
        if (e === NERWORK_ERROR) {
          esc.showError(e);
        } else if (code === 'ACCOUNT_NOT_VERIFIED' || e.message == UNVERIFIED_ERROR) {
          // Not verified: route into the OTP/verify flow instead of a generic error.
          storeDetailsAndSendOTP(e.phone, true, e.isSourceApp, {
            username: trimSpace(email).toLowerCase(),
            password,
          });
        } else if (code === 'ACCOUNT_DISABLED' || e.message == DISABLE_ERROR) {
          asc.setNavigationRef(navigation);
          asc.setVisible(true);
        } else if (code === 'INVALID_CREDENTIALS') {
          // Wrong email/password — show the accurate form-level message, not the
          // password-*requirements* validation string (audit P1: misleading copy).
          setError(true);
        } else {
          setError(true);
        }
        updateNumAttempts();
      });
  };

  async function storeDetailsAndSendOTP(
    mobileNumber,
    fromLogin,
    isSourceApp,
    authCredentials,
  ) {
    try {
      //untransforming mobile number before processing
      const unTransformedMobileNumber = mobileNumber.replace(/-/g, '');

      //storing mobile number in local storage for further process
      await storeData(KEYS.phoneNumber, unTransformedMobileNumber);

      //storing email and password in keychain for resend otp api call
      await Keychain.setGenericPassword(
        trimSpace(email).toLowerCase(),
        password,
      );

      getOTP(
        unTransformedMobileNumber,
        navigation,
        setBottomModalVisible,
        lpc,
        esc,
        undefined,
        fromLogin,
        isSourceApp,
        authCredentials,
      );
    } catch (e) {
      setTimeout(() => {
        setBottomModalVisible(true);
      }, 1000);
    }
  }

  const onPressRequestFailed = () => setBottomModalVisible(false);

  const onPressFaceId = async () => {
    if (!supportFaceId) {
      setshowModal(true);
    } else {
      // Only allow Face ID if the checkbox is checked
      const isFaceIdEnabled = await getData(KEYS.faceIdEnabled);
      if (isFaceIdEnabled === 'true' && haveCreds) {
        authenticateFaceId();
      } else {
        setshowModal(true);
      }
    }
  };

  const authenticateFaceId = () => {
    ReactNativeBiometrics.simplePrompt({
      promptMessage:
        Platform.OS == 'ios'
          ? 'Identifying Face ID...'
          : 'Identifying Biometrics...',
    })
      .then(async resultObject => {
        const {success} = resultObject;
        if (success) {
          const credentials = await Keychain.getGenericPassword();
          if (credentials) {
            const postData = {
              email: trimSpace(credentials.username),
              password: credentials.password,
            };
            userFaceSignin(postData);
          }
        } else {
          // Dlog('user cancelled biometric prompt');
          setShowFaceIdFailedModal(true);
        }
      })
      .catch(() => {
        // Dlog('biometrics failed');
        setShowFaceIdFailedModal(true);
      });
  };

  const userFaceSignin = postData => {
    lpc.setVisible(true); //shows up evn without this
    fetchApi(HTTP_METHODS.POST, LOGIN, postData)
      .then(async res => {
        if (res.status === 200) {
          storeData(KEYS.signinNumAttemptKey, '0');

          const profile = res.data.data;
          await storeData(KEYS.userToken, profile.accessToken || profile.token);
          if (profile.refreshToken) {
            await setRefreshToken(profile.refreshToken);
          }
          await storeData(KEYS.isLogin, USER_AUTH_STATUS.login);
          await storeData(KEYS.userId, profile.id);
          await storeUserProfileData(profile);
          asc.setOnboarded(!!profile.coachingOnboarded);
          lpc.setVisible(false);
          setTimeout(() => {
            setFullName(profile.firstname + ' ' + profile.lastname);
            setShowFaceIdSuccessModal(true);
            setTimeout(() => {
              setShowFaceIdSuccessModal(false);
              navigation.dispatch(
                CommonActions.reset({
                  index: 0,
                  routes: [{name: 'HomeTab'}],
                }),
              );
            }, 1500);
          }, 800);
        } else {
          updateNumAttempts();
          lpc.setVisible(false);
        }
      })
      .catch(e => {
        updateNumAttempts();
        lpc.setVisible(false);
        esc.showError(e);
      });
  };

  const validateSignin = () => {
    if (isEmpty(email) || isEmpty(password)) {
      setValidForm(false);
      return;
    }

    setValidForm(true);
  };

  return (
    <SafeAreaView
      style={styles.container}
      testID={tID('SignIn-SafeAreaView-Main')}>
      <ErrorBottomExpandableDrawer
        visible={bottomModalVisible}
        onClose={onPressRequestFailed}
      />
      {Platform.OS != 'web' && (
        <View
          style={styles.headerView}
          testID={tID('SignIn-View-HeaderContainer')}>
          <Header
            title="Sign In"
            onPressLeftIcon={() => navigation.goBack()}
            testID={tID('SignIn-Header-SignIn')}
          />
        </View>
      )}
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : null}
        testID={tID('SignIn-KeyboardAvoidingView-1')}
        style={styles.container}>
        <ScrollView keyboardShouldPersistTaps="handled"
          style={styles.scrollView}
          testID={tID('SignIn-ScrollView-1')}
          contentContainerStyle={[
            styles.scrollviewContainer,
            {paddingBottom: 20},
          ]}>
          <Text
            style={styles.title}
            color={colors.textPrimary}
            testID={tID('SignIn-Text-Title')}>
            Welcome Back
          </Text>
          <Text
            style={styles.subtitle}
            color={colors.textSecondary}
            testID={tID('SignIn-Text-SubTitle')}>
            Access your Diathrive Health account by signing in below.
          </Text>
          <Divider top={16} />
          <SignInInputForm
            hasError={hasError}
            hasEmailError={hasEmailError}
            hasPasswordError={hasPasswordError}
            errorText="*Could not log you in. Please check your email and password."
            isValidForm={isValidForm}
            showBiometricOption={supportFaceId}
            checkboxSelected={faceIDCheckBoxSelected}
            onPressCheckbox={onPressCheckbox}
            showPassword={showPassword}
            onPressShowPassword={onPressShowPassword}
            email={email}
            password={password}
            onChangeEmail={setEmail}
            onChangePassword={setPassword}
          />
          <Pressable
            style={styles.alignLeft}
            onPress={onPressForgotPassword}
            testID={tID('SignIn-Pressable-ForgotPassword')}>
            <Text style={styles.fotgotPasswordText}>Forgot Password?</Text>
          </Pressable>

          {/* Saved credentials alone aren't enough — the device must also
              report a usable biometric, or this button dead-ends in the
              "disabled" drawer. */}
          {haveCreds && supportFaceId && (
            <Button
              title={getBiometricLabel()}
              topMargin={47}
              testID={tID('SignIn-Button-FaceID')}
              onPress={() => checkAttempts(onPressFaceId)}
            />
          )}

          <Button
            disabled={!isValidForm}
            buttonType={BUTTON_TYPES.Filled}
            titleColor={colors.textOnAccent}
            title={Platform.OS === 'web' ? 'Continue' : 'Sign In'}
            testID={tID('SignIn-Button-SignIn')}
            topMargin={haveCreds && supportFaceId ? 16 : 47}
            onPress={() => checkAttempts(onPressSignin)}
            style={Platform.OS === 'web' && styles.flex1Width292}
          />
          <Text style={styles.signupText}>
            Don't have an account?{' '}
            <Text
              testID={tID('SignIn-Text-SignUpText')}
              style={styles.signupLinkText}
              onPress={onPressSignUp}>
              Sign Up Now
            </Text>
          </Text>
          <View style={{height: 20}} />
        </ScrollView>
      </KeyboardAvoidingView>
      <ContactSupportPopup
        showContact={showContact}
        setShowContact={setShowContact}
      />
      <FaceIdDisableDrawer
        visible={showModal}
        onClose={() => setshowModal(false)}
        onPressCancel={() => setshowModal(false)}
        onPressMySetttings={() => Linking.openSettings()}
      />

      <FaceIdIdentfyingDrawer
        visible={showIdentifyingModal}
        onClose={() => setShowIdentifyingModal(false)}
      />

      <FaceIdSuccess
        visible={showFaceIdSuccessModal}
        fullName={fullName}
        onClose={() => setShowFaceIdSuccessModal(false)}
      />

      <FaceIdFailed
        visible={showFaceIdFailedModal}
        onTryAgain={onPressTryFaceIdAgain}
        onClose={() => setShowFaceIdFailedModal(false)}
      />

      <FaceIdNotRecognized
        visible={showFaceIdNotRecognzedModal}
        onClose={() => setShowFaceIdNotRecognzedModal(false)}
      />
      <AccountStatusModal visible={visible} shouldCloseModal={setVisible} />
    </SafeAreaView>
  );
};

export default SignIn;

//The added testIds have the following structure
// SignIn-SafeAreaView-Main
// SignIn-KeyboardAvoidingView-1
// SignIn-ScrollView-1
// SignIn-View-HeaderContainer
// SignIn-Header-SignIn
// SignIn-Text-Title
// SignIn-Text-SubTitle
// SignIn-Pressable-ForgotPassword
// SignIn-Text-SignUpText
// SignIn-Text-SignUpLinkText
// SignIn-Button-SignIn
// SignIn-Button-FaceID
// SignIn-Popup-ContactSupport
// SignIn-Button-ContactSupportClose
